import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Cell,
} from 'recharts';

const PLATFORM_COLORS = [
    CHART_COLORS.chart.line1,
    CHART_COLORS.chart.line2,
    CHART_COLORS.chart.line3,
    CHART_COLORS.chart.line4,
    CHART_COLORS.chart.line5,
    CHART_COLORS.chart.line6,
];

export const ROASByPlatformChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, channels } = useBoardChartData();

    const chartData = useMemo(() => {
        if (!finance?.breakdown) return [];

        // Total revenue from all sources (used as numerator for per-platform ROAS)
        const totalRevenue = Number(finance.totalRevenues) || 0;

        // Compute ad spend per channel
        const adBreakdown = finance.breakdown.filter(
            (item: any) => ['advertising', 'ad_spend', 'marketing'].includes(item.category)
        );

        const spendByChannel: Record<string, number> = {};
        for (const item of adBreakdown) {
            if (item.type === 'EXPENSE') {
                const chId = item.channelId as string;
                spendByChannel[chId] = (spendByChannel[chId] || 0) + Math.abs(Number(item.value) || 0);
            }
        }

        const channelMap: Record<string, string> = {};
        if (channels) {
            for (const ch of channels) {
                channelMap[ch.id] = ch.channelType || ch.id;
            }
        }

        // Total ad spend across all platforms
        const totalAdSpend = Object.values(spendByChannel).reduce((sum, v) => sum + v, 0);

        const data = Object.keys(spendByChannel).map((chId, index) => {
            const spend = spendByChannel[chId];
            // Attribute revenue proportionally based on each platform's share of total ad spend
            const revenueShare = totalAdSpend > 0 ? (spend / totalAdSpend) * totalRevenue : 0;
            const roas = spend > 0 ? revenueShare / spend : 0;
            return {
                name: channelMap[chId] || chId,
                roas: Math.round(roas * 100) / 100,
                spend,
                revenue: Math.round(revenueShare * 100) / 100,
                fill: PLATFORM_COLORS[index % PLATFORM_COLORS.length],
            };
        });

        return data.sort((a, b) => b.roas - a.roas);
    }, [finance?.breakdown, finance?.totalRevenues, channels]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartData}
                        margin={CHART_STYLES.margin.withAxis}
                    >
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="name"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickMargin={CHART_STYLES.axis.tickMargin}
                        />
                        <YAxis
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(value: number) => `${value}x`}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            formatter={(value: number) => [
                                `${value}x`,
                                t('charts.roas', 'ROAS'),
                            ]}
                            labelFormatter={(label: string) => label}
                        />
                        <Bar
                            dataKey="roas"
                            radius={CHART_STYLES.bar.radius}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        >
                            {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
